import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import axios from "../../api/axiosInstance";
import {
  FaUserEdit,
  FaSlidersH,
  FaCog,
  FaChevronLeft,
  FaChevronRight,
  FaCamera,
  FaHome,
  FaHeart,
  FaSignOutAlt,
} from "react-icons/fa";
import { BookOpen } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const ProfileDrawer = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const fileInputRef = useRef(null);
  const location = useLocation();

  // Auto-close sidebar on route change (mobile only)
  useEffect(() => {
    if (isMobileOpen) setIsMobileOpen(false);
  }, [location]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    const userId = user?.id;

    if (!userId) {
      toast.error("User ID not found in session.");
      setLoading(false);
      return;
    }

    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`/api/matrimony-profile/${userId}/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        });
        setProfile(res.data);
      } catch (error) {
        toast.error("Failed to fetch profile details");
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  const handleImageClick = () => {
    if (!uploading) fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("photo", file);

    try {
      setUploading(true);
      const user = JSON.parse(localStorage.getItem("user"));
      const userId = user?.id;

      const response = await axios.patch(
        `/api/matrimony-profileupload/${userId}/`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      toast.success("Profile photo updated!");
      setProfile(response.data);
    } catch (error) {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/login";
  };

  return (
    <>
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white border-b shadow z-30 flex items-center justify-between px-4 py-2">
        <div
          className="flex items-center space-x-2 cursor-pointer"
          onClick={handleImageClick}
        >
          <img
            src={profile?.photo || require("../../assets/default_image.webp")}
            alt="Profile"
            className="w-10 h-10 rounded-full object-cover border"
          />
          <span className="font-semibold text-gray-700">
            {profile?.user?.first_name}
          </span>
        </div>
        <button
          onClick={() => setIsMobileOpen(true)}
          className="p-2 rounded-md bg-gray-100 hover:bg-gray-200"
        >
          <FaChevronRight />
        </button>
      </div>

      {/* Sidebar (Desktop + Mobile Drawer) */}
      <div
        className={`fixed top-0 left-0 h-screen bg-white border-r shadow z-40 transition-all duration-300
          ${isCollapsed ? "w-20" : "w-72"}
          ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0`}
      >
        {/* Collapse Button (Desktop only) */}
        {/* <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-4 top-4 bg-white border rounded-full shadow p-1 hover:bg-gray-50 hidden md:block"
        >
          {isCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
        </button> */}

        <div className="flex flex-col h-full">
          {/* Profile Info */}
          {loading ? (
            <div className="flex flex-col items-center justify-center flex-1 p-4">
              <div className="w-16 h-16 rounded-full bg-gray-200 animate-pulse mb-2" />
              {!isCollapsed && (
                <>
                  <div className="w-24 h-4 bg-gray-200 animate-pulse mb-1 rounded" />
                  <div className="w-16 h-3 bg-gray-200 animate-pulse rounded" />
                </>
              )}
            </div>
          ) : profile ? (
            <div className="p-4 flex flex-col items-center border-b">
              <div
                className="relative group cursor-pointer"
                onClick={handleImageClick}
              >
                <img
                  src={
                    profile.photo || require("../../assets/default_image.webp")
                  }
                  alt={profile.user.first_name}
                  className={`rounded-full border object-cover transition ${
                    uploading ? "opacity-50" : ""
                  }`}
                  style={{ width: "64px", height: "64px" }}
                />
                <div className="absolute bottom-0 right-0 bg-white rounded-full p-1 border group-hover:bg-gray-100 transition">
                  <FaCamera className="text-gray-600 text-sm" />
                </div>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
              {!isCollapsed && (
                <>
                  <h4 className="font-semibold mt-2">
                    {profile.user.first_name} {profile.user.last_name}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {profile.profile_id || `ID: ${profile.user.id}`}
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="text-center p-4 text-sm text-gray-500">
              Profile not found
            </div>
          )}

          {/* Actions */}
          <div className="flex-1 flex flex-col p-2 space-y-2 mt-4">
            {[
              { to: "", icon: <FaHome />, label: "Home" },
              {
                to: "profile-update",
                icon: <FaUserEdit />,
                label: "Edit Profile",
              },
              {
                to: "user-preference",
                icon: <FaSlidersH />,
                label: "Edit Preferences",
              },
              {
                to: "/matches-all--php-html",
                icon: <FaHeart />,
                label: "All Matches",
              },
              {
                to: "success-story",
                icon: <BookOpen size={18} />,
                label: "Success Story",
              },
              { to: "all-settings", icon: <FaCog />, label: "Settings" },
            ].map(({ to, icon, label }) => (
              <Link
                key={to}
                to={to}
                className="flex items-center text-decoration-none justify-center md:justify-start space-x-2 p-2 rounded hover:bg-gray-100 w-full"
              >
                {icon}
                {!isCollapsed && <span>{label}</span>}
              </Link>
            ))}

            <button
              onClick={handleLogout}
              className="flex items-center justify-center md:justify-start space-x-2 p-2 rounded hover:bg-gray-100 w-full"
            >
              <FaSignOutAlt />
              {!isCollapsed && <span>Sign out</span>}
            </button>
          </div>
        </div>
      </div>

      {/* Overlay for Mobile Drawer */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 md:hidden"
          onClick={() => setIsMobileOpen(false)}
        ></div>
      )}
    </>
  );
};

export default ProfileDrawer;
